import { Fragment, useCallback, useMemo } from 'react';
import { __OPERATION__EVENT__ADMIN__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { useEffect } from 'react';
import { FiltersFormErrorEvents, ErrorEventsClient } from './types';
import { useFormik } from 'formik';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValues } from '..';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonErrorEventsCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsObj, validationSchemaErrorEventsFilter, headCells } from './constants';
import PmiTitle from 'shell/components/shared/title';
import FiltersErrorEvents from 'system/components/error-events/list';
import { EventsError, transformEventsError } from '../utils/transform-events-error';
import { transformDataset } from './utils';
import { EventError } from 'shell/models/event-error';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { Typography, useEventCallback } from '@mui/material';

const ErrorEventsListPage = (): React.ReactElement => {
  const [eventsError, getEventsError] = useHttpFetch<HttpResponseList<EventError>, EventsError>(
    `${__OPERATION__EVENT__ADMIN__}/eventoInErrore/list`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: transformEventsError,
    }
  );
  const filterTable = useFilterTable<FiltersFormErrorEvents>();

  const onSubmit = useEventCallback((formValues: FiltersFormErrorEvents) => {
    filterErrorEventsForm.setFieldValue('page', 0);

    filterTable.setValues(formValues);

    const queryString = transformDataset({ ...formValues, page: 0 });

    getEventsError({
      params: {},
      paramsSerializer: () => queryString,
    });
  });

  const filterErrorEventsForm = useFormik<FiltersFormErrorEvents>({
    initialValues: initialValues,
    validationSchema: validationSchemaErrorEventsFilter,
    onSubmit,
    enableReinitialize: true,
  });

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  useEffect(() => {
    filterErrorEventsForm.setValues(filterTable.values || initialValues);
    const queryString = transformDataset(filterTable.values || initialValues);
    getEventsError({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  const onVisibilityPress = useCallback(
    (cell): Promise<ErrorEventsClient> => {
      return new Promise((resolve) => {
        navigate(
          `/sistema/eventmanager/ricerca-eventi-in-errore/dettaglio-evento-in-errore/${cell.id}`
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      const newDataset = { ...filterErrorEventsForm.values, page: newPage || 0 };
      const queryString = transformDataset(newDataset);
      (
        getEventsError({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<EventError>, EventsError>
      ).then(() => {
        filterErrorEventsForm.setFieldValue('page', newPage);
      });
    },
    [getEventsError, filterErrorEventsForm.values, filterErrorEventsForm.setValues]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterErrorEventsForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      const queryString = transformDataset(newDataset);
      (
        getEventsError({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<EventError>, EventsError>
      ).then(() => {
        filterErrorEventsForm.setValues(newDataset);
      });
    },
    [getEventsError, filterErrorEventsForm.setFieldValue, filterErrorEventsForm.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterErrorEventsForm.values, orders };
      const queryString = transformDataset(newDataset);
      (
        getEventsError({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<EventError>, EventsError>
      ).then(() => {
        filterErrorEventsForm.setFieldValue('orders', orders);
      });
    },
    [getEventsError, filterErrorEventsForm.values, filterErrorEventsForm.setValues]
  );

  const count: number = useMemo(() => {
    return (eventsError.data as EventsError)?.pageInfo?.totalCount || 0;
  }, [eventsError.data]);
  return (
    <Fragment>
      <Breadcrumbs items={CommonErrorEventsCrumbs} />
      <PmiTitle title="Ricerca Eventi In Errore" />
      <div className="pmi-my-3">
        <FiltersErrorEvents filterErrorEventsForm={filterErrorEventsForm} />
      </div>
      {eventsError.data ? (
        (eventsError.data as EventsError)?.eventsError?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterErrorEventsForm.values.orders}
            activeActions={auths}
            isServer
            page={filterErrorEventsForm.values.page}
            rowsPerPage={filterErrorEventsForm.values.rowsPerPage}
            body={(eventsError.data as EventsError)?.eventsError}
            onVisibilityPress={onVisibilityPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Ricerca Eventi In Errore"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

ErrorEventsListPage.displayName = 'Error Events List page component';

export default ErrorEventsListPage;
